import axios from 'axios'

const API_URL = process.env.REACT_APP_THEME_API_URL || 'http://localhost:8000'

export interface ExternalCalendar {
  id: number
  user_id: number
  name: string
  ical_url: string
  color: string
  is_active: boolean
  last_sync_at: string | null
  created_at: string
  updated_at: string
}

export interface CreateExternalCalendarRequest {
  name: string
  ical_url: string
  color?: string
  is_active?: boolean
}

export interface ExternalCalendarEvent {
  id: string
  title: string
  start: string
  end: string
  allDay: boolean
  description?: string
  location?: string
  calendarId: number
  calendarName: string
  color: string
}

export interface ICalTestResult {
  valid: boolean
  events?: any[]
  error?: string
  events_count?: number
  security_warnings?: string[]
  security_score?: number
  url_used?: string
}

class ExternalCalendarApiService {
  /**
   * Lista todos os calendários externos do usuário
   */
  async getCalendars(): Promise<ExternalCalendar[]> {
    try {
      const response = await axios.get(`${API_URL}/api/external-calendars`)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar calendários externos:', error)
      throw error
    }
  }

  /**
   * Cria um novo calendário externo
   */
  async createCalendar(data: CreateExternalCalendarRequest): Promise<ExternalCalendar> {
    try {
      const response = await axios.post(`${API_URL}/api/external-calendars`, data)
      return response.data
    } catch (error) {
      console.error('Erro ao criar calendário externo:', error)
      throw error
    }
  }

  /**
   * Atualiza um calendário externo
   */
  async updateCalendar(id: number, data: Partial<CreateExternalCalendarRequest>): Promise<ExternalCalendar> {
    try {
      const response = await axios.put(`${API_URL}/api/external-calendars/${id}`, data)
      return response.data
    } catch (error) {
      console.error('Erro ao atualizar calendário externo:', error)
      throw error
    }
  }

  /**
   * Remove um calendário externo
   */
  async deleteCalendar(id: number): Promise<void> {
    try {
      await axios.delete(`${API_URL}/api/external-calendars/${id}`)
    } catch (error) {
      console.error('Erro ao remover calendário externo:', error)
      throw error
    }
  }

  /**
   * Sincroniza um calendário específico
   */
  async syncCalendar(id: number): Promise<void> {
    try {
      await axios.post(`${API_URL}/api/external-calendars/${id}/sync`)
    } catch (error) {
      console.error('Erro ao sincronizar calendário:', error)
      throw error
    }
  }

  /**
   * Sincroniza todos os calendários do usuário
   */
  async syncAllCalendars(): Promise<void> {
    try {
      await axios.post(`${API_URL}/api/external-calendars/sync-all`)
    } catch (error) {
      console.error('Erro ao sincronizar todos os calendários:', error)
      throw error
    }
  }

  /**
   * Busca eventos de todos os calendários externos
   */
  async getEvents(startDate?: string, endDate?: string): Promise<ExternalCalendarEvent[]> {
    try {
      const params = new URLSearchParams()
      if (startDate) params.append('start', startDate)
      if (endDate) params.append('end', endDate)
      
      const response = await axios.get(`${API_URL}/api/external-calendars/events?${params}`)
      return response.data
    } catch (error) {
      console.error('Erro ao buscar eventos de calendários externos:', error)
      throw error
    }
  }

  /**
   * Testa se uma URL iCal é válida
   */
  async testIcalUrl(icalUrl: string): Promise<ICalTestResult> {
    try {
      const response = await axios.post(`${API_URL}/api/external-calendars/test-url`, {
        ical_url: icalUrl
      })
      return response.data
    } catch (error: any) {
      console.error('Erro ao testar URL iCal:', error)
      return {
        valid: false,
        error: error.response?.data?.message || 'Erro ao testar URL'
      }
    }
  }
}

export const externalCalendarApiService = new ExternalCalendarApiService()
